import { useCallback, useEffect } from "react";

import type { CompiledChapter } from "@/data/schema/compiled";
import type { PlaybackState } from "@/lib/timeline/playback";
import {
  readTimelineSession,
  writeTimelineSession,
  type TimelineCameraFrame,
} from "@/lib/timeline/view/TimelineSession/session";
import { isRecoverableTimelineView } from "@/lib/timeline/view/TimelineViewRecovery/recovery";

/** Write the camera frame shortly after it changes. */
export const CAMERA_FRAME_DEBOUNCE_MS = 200;

export interface TimelineSessionRestore {
  playheadMs: number;
  speed: number;
  view: TimelineCameraFrame | null;
  heldZoom: number | null;
  panHeld: boolean;
}

export function readTimelineSessionRestore(
  chapter: CompiledChapter,
): TimelineSessionRestore | null {
  const snapshot = readTimelineSession(chapter.bookId, chapter.chapterId);
  if (snapshot === null) {
    return null;
  }
  const view: TimelineCameraFrame = {
    zoom: snapshot.zoom,
    panX: snapshot.panX,
    panY: snapshot.panY,
    heldZoom: snapshot.heldZoom,
    panHeld: snapshot.panHeld,
  };
  if (!isRecoverableTimelineView(view)) {
    writeTimelineSession({
      ...snapshot,
      zoom: 1,
      panX: 0,
      panY: 0,
      heldZoom: null,
      panHeld: false,
    });
    return {
      playheadMs: snapshot.playheadMs,
      speed: snapshot.speed,
      view: null,
      heldZoom: null,
      panHeld: false,
    };
  }
  return {
    playheadMs: snapshot.playheadMs,
    speed: snapshot.speed,
    view,
    heldZoom: snapshot.heldZoom,
    panHeld: snapshot.panHeld,
  };
}

function cameraFrameKey(
  playback: PlaybackState,
  frame: TimelineCameraFrame,
): string {
  return [
    playback.playheadMs,
    playback.speed,
    frame.zoom,
    frame.panX,
    frame.panY,
    frame.heldZoom ?? "",
    frame.panHeld,
  ].join(":");
}

export function useTimelineSessionPersist(
  chapter: CompiledChapter,
  getPlayback: () => PlaybackState,
  getCameraFrame: () => TimelineCameraFrame,
): void {
  const flush = useCallback((): void => {
    const playback = getPlayback();
    const frame = getCameraFrame();
    if (!isRecoverableTimelineView(frame)) {
      return;
    }
    writeTimelineSession({
      bookId: chapter.bookId,
      chapterId: chapter.chapterId,
      playheadMs: playback.playheadMs,
      speed: playback.speed,
      zoom: frame.zoom,
      panX: frame.panX,
      panY: frame.panY,
      heldZoom: frame.heldZoom,
      panHeld: frame.panHeld,
    });
  }, [chapter.bookId, chapter.chapterId, getCameraFrame, getPlayback]);

  useEffect(() => {
    let debounceId = 0;
    let lastKey = "";
    const schedule = (): void => {
      if (debounceId !== 0) {
        return;
      }
      debounceId = window.setTimeout(() => {
        debounceId = 0;
        flush();
      }, CAMERA_FRAME_DEBOUNCE_MS);
    };
    const watchId = window.setInterval(() => {
      const key = cameraFrameKey(getPlayback(), getCameraFrame());
      if (key === lastKey) {
        return;
      }
      lastKey = key;
      schedule();
    }, 50);
    const onHide = (): void => {
      window.clearTimeout(debounceId);
      flush();
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.clearInterval(watchId);
      window.clearTimeout(debounceId);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
      flush();
    };
  }, [flush, getCameraFrame, getPlayback]);
}
